import { inject, makeStateKey } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { Item, ItemService } from '../../pages/item-detail/item-detail.service';
import { CarouselService } from '../../pages/home/home-carousel2/carousel.service';
import { catchError, of, tap } from 'rxjs';
import { PrerenderDataService } from '../../prerender-data.service';

export const carouselResolver: ResolveFn<Object> = (route, state) => {
  const prerenderDataService = inject(PrerenderDataService);
  const key = makeStateKey<any>('CAROUSEL');

  const cached = prerenderDataService.getPreloadedData(key);
  if (cached !== null) {
    return of(cached);
  }

  return inject(CarouselService).getWebCarousels().pipe(
    tap((data) => {
      // Set the prerender state on both server and client to act as an instant cache
      prerenderDataService.setPreloadedData(key, data);
    }),
    catchError((err) => {
      console.error('Error fetching carousels in carouselResolver:', err);
      return of([]);
    })
  );
}

export const itemResolver: ResolveFn<Object> = (route, state) => {
  const prerenderDataService = inject(PrerenderDataService);
  const key = makeStateKey<any>('CAT_LIST');
  const getAllCatsKey = makeStateKey<any>('GET_ALL_CATS');

  const cached = prerenderDataService.getPreloadedData(getAllCatsKey) || prerenderDataService.getPreloadedData(key);
  if (cached !== null && Array.isArray(cached) && cached.length > 0) {
    return of(cached);
  }

  return inject(ItemService).getAllCats().pipe(
    tap((data) => {
      prerenderDataService.setPreloadedData(key, data);
      prerenderDataService.setPreloadedData(getAllCatsKey, data);
    }),
    catchError((err) => {
      console.error('Error fetching categories in itemResolver:', err);
      return of([]);
    })
  );
}

export const itemResolverAdvance: ResolveFn<Object> = (route, state) => {
  const cat = route.paramMap.get('cat');
  const prerenderDataService = inject(PrerenderDataService);
  const key = makeStateKey<any>('CAT_ITEMS_' + cat);

  const cached = prerenderDataService.getPreloadedData(key);
  if (cached !== null && Array.isArray(cached) && cached.length > 0) {
    return of(cached);
  }

  return inject(ItemService).getItemAll(cat!).pipe(
    tap((data) => {
      prerenderDataService.setPreloadedData(key, data);
    }),
    catchError((err) => {
      console.error(`Error fetching items for category ${cat} in itemResolverAdvance:`, err);
      return of([]);
    })
  );
}

export const productResolver: ResolveFn<Object> = (route, state) => {
  const productID = String(route.paramMap.get('slug')).split('_')[1];
  const prerenderDataService = inject(PrerenderDataService);
  const key = makeStateKey<any>('PROD_' + productID);

  const cached = prerenderDataService.getPreloadedData(key);
  if (cached !== null) {
    return of(cached);
  }

  return inject(ItemService).getItem(productID).pipe(
    tap((data) => {
      prerenderDataService.setPreloadedData(key, data);
    }),
    catchError((err) => {
      console.error(`Error fetching product ID ${productID} in productResolver:`, err);
      return of({});
    })
  );
}

export interface HomeData {
  deals: Item[];
  newArrivals: Item[];
}

export const homeDataResolver: ResolveFn<HomeData> = (route, state) => {
  const prerenderDataService = inject(PrerenderDataService);
  const itemService = inject(ItemService);
  const dealsKey = makeStateKey<Item[]>('DEALS');
  const newKey = makeStateKey<Item[]>('NEW');
  const homeDataKey = makeStateKey<HomeData>('HOME_DATA');

  const cachedData = prerenderDataService.getPreloadedData(homeDataKey);
  if (cachedData && cachedData.deals && cachedData.newArrivals && cachedData.deals.length > 0) {
    return of(cachedData);
  }

  const cachedDeals = prerenderDataService.getPreloadedData(dealsKey);
  const cachedNew = prerenderDataService.getPreloadedData(newKey);
  if (cachedDeals !== null && cachedNew !== null && Array.isArray(cachedDeals) && Array.isArray(cachedNew) && cachedDeals.length > 0) {
    const result: HomeData = { deals: cachedDeals, newArrivals: cachedNew };
    prerenderDataService.setPreloadedData(homeDataKey, result);
    return of(result);
  }

  return itemService.getHomeDealsAndNewArrivals().pipe(
    tap((data) => {
      prerenderDataService.setPreloadedData(dealsKey, data.deals);
      prerenderDataService.setPreloadedData(newKey, data.newArrivals);
      prerenderDataService.setPreloadedData(homeDataKey, data);
    }),
    catchError((err) => {
      console.error('Error in homeDataResolver:', err);
      return of({ deals: [], newArrivals: [] });
    })
  );
};